import { Box, Button, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { CreateIcon, TrashIcon } from 'icons';
import { useEffect, useState } from 'react';
import { type UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { isEmailValid } from '../../../helpers';
import useSsoDomainCheck from '../../../hooks/useSsoDomainCheck';

type Props = UseControllerProps & {
  mode: 'multiple_inputs' | 'textarea';
};

export const EmailsListField = ({ mode, name }: Props) => {
  const { setValue, watch, clearErrors, getFieldState } = useFormContext();
  const ssoDomainCheck = useSsoDomainCheck();

  const value = watch(name);
  if (!Array.isArray(value)) throw new Error('Value must be an array');

  const [length, setLength] = useState(value.length);
  const [textareaValue, setTextareaValue] = useState(value.join(', '));
  const allFieldsCorrect = value.every(
    email => z.string().email().safeParse(email).success,
  );

  const { t } = useTranslation('common');

  const onSingleFieldChange = (fieldIndex: number, fieldValue: string) => {
    clearErrors(`${name}.${fieldIndex}`);
    setValue(`${name}.${fieldIndex}`, fieldValue);
    setTextareaValue(tV => {
      const tVArray = tV.split(',');
      tVArray[fieldIndex] = fieldValue;
      return tVArray.map(v => v.trim()).join(', ');
    });
  };

  const removeField = (index: number) => {
    setValue(
      name,
      value.filter((_, i) => i !== index),
    );
    setLength(l => l - 1);
  };

  const parseTextareaValue = (v: string) =>
    v.split(',').map(email => email.trim());

  useEffect(() => {
    setLength(value.length);
    setTextareaValue(value.join(', '));
  }, [value.length]);

  const getErrors = () => {
    const errorsObj: any = getFieldState(name).error;
    return Array.isArray(errorsObj)
      ? errorsObj.map(e => e.message).join('\n')
      : null;
  };

  return mode === 'multiple_inputs' ? (
    <>
      <Stack spacing="md">
        {Array.from({ length }, (_, i) => `email-list-field-${i}`).map(
          (key, i) => {
            const email = watch(`${name}.${i}`);

            return (
              <Stack key={key} spacing="xs">
                <TextInput
                  value={watch(`${name}.${i}`)}
                  error={getFieldState(`${name}.${i}`).error?.message}
                  onChange={e => onSingleFieldChange(i, e.target.value)}
                  placeholder={t('emailsListField.multipleInputs.placeholder')}
                  rightSection={
                    length > 1 && (
                      <TrashIcon
                        size={20}
                        color="red"
                        onClick={() => removeField(i)}
                      />
                    )
                  }
                />
                {isEmailValid(email) &&
                  !ssoDomainCheck(watch(`${name}.${i}`)) && (
                    <Box px={16} py={2}>
                      <Text fz="sm" color="orange">
                        {t('emailsListField.emailValidation.ssoDomainWarning')}
                      </Text>
                    </Box>
                  )}
              </Stack>
            );
          },
        )}
      </Stack>
      <Button
        onClick={() => setValue(name, [...value, ''])}
        variant="subtle"
        color="green"
        leftIcon={<CreateIcon size={20} />}
        disabled={!allFieldsCorrect}
      >
        {t('emailsListField.multipleInputs.addButton')}
      </Button>
    </>
  ) : (
    <Textarea
      minRows={4}
      value={textareaValue}
      onChange={e => setTextareaValue(e.target.value)}
      onBlur={() => setValue(name, parseTextareaValue(textareaValue))}
      placeholder={t('emailsListField.textarea.placeholder')}
      error={getErrors()}
    />
  );
};
